interface Location {
    latitude: string;
    longitude: string;
}

interface MapBounds {
    center: { latitude: string; longitude: string };
    zoom: number;
}

export const calculateMapBounds = (locations: Location[], mapWidth = 1000, mapHeight = 600): MapBounds => {
    if (locations.length === 0) {
        return { center: { latitude: '0', longitude: '0' }, zoom: 2 };
    }

    if (locations.length === 1) {
        return { center: locations[0], zoom: 15 };
    }

    const coords = locations.map((loc) => ({
        lat: parseFloat(loc.latitude),
        lng: parseFloat(loc.longitude),
    }));

    const lats = coords.map((c) => c.lat);
    const lngs = coords.map((c) => c.lng);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    const latDelta = maxLat - minLat;
    const lngDelta = maxLng - minLng;

    // Calculate zoom based on viewport size
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat: number) {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const latFraction = (latRad(maxLat) - latRad(minLat)) / Math.PI;
    const lngFraction = lngDelta / 360;

    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

    const finalZoom = Math.min(latZoom, lngZoom, ZOOM_MAX) - 1; // -1 for padding

    return {
        center: {
            latitude: centerLat.toString(),
            longitude: centerLng.toString(),
        },
        zoom: Math.max(1, finalZoom),
    };
};
